import { takeLeading, call, put, select } from "redux-saga/effects";
import {
  stickersSetAll,
  stickerAddOne,
  stickerUpdateOne,
  stickerRemoveOne,
  addChosenSticker,
  removeChosenSticker,
} from "../slices/stickers.slice";
import { loadingEdit, errorEdit, successEdit } from "../slices/status.slice";
import { stickersService, studentsService } from "../../services";
import { modalEdit, contentEdit } from "../slices/modal.slice";
import {
  addOneToHistory,
  removeOneFromHistory,
  changeHistory,
} from "../slices/events.slice";
import { enrichStickerWithColors } from "./index";
import { toast } from "react-toastify";
import { changeNotificationHistory } from "../slices/events.slice";
import { studentAddOne, studentUpdateOne } from "../slices/students.slice";

export function* getAll(action) {
  yield put(loadingEdit(true));
  try {
    const response = yield call(stickersService.getAll, {
      userId: action.payload.userId,
      boardId: action.payload.boardId,
    });
    yield put(stickersSetAll(response.data));
  } catch (error) {
    if (error.response) {
      yield put(errorEdit(error.response.data.message));
    } else {
      yield put(errorEdit("Something has gone wrong. Please try again later"));
    }
  } finally {
    yield put(loadingEdit(false));
  }
}

export function* addOne(action) {
  yield put(loadingEdit(true));
  try {
    const response = yield call(stickersService.create, action.payload.body);
    let newSticker = response.data.sticker;

    newSticker = yield* enrichStickerWithColors(newSticker);

    yield put(stickerAddOne(newSticker));

    const { teacherLevelAssignment } = response.data || {};
    if (teacherLevelAssignment && teacherLevelAssignment !== "ok") {
      yield put(
        changeNotificationHistory({
          ...action.payload.body,
          notification: teacherLevelAssignment,
        })
      );
      toast.warn(teacherLevelAssignment);
    }

    if (!action.payload.isUndo) {
      yield put(
        addOneToHistory({
          reversedType: "STICKER_REMOVE_ONE",
          eventBody: newSticker,
        })
      );
      yield put(successEdit("New class has been created"));
    } else {
      yield put(
        changeHistory({
          newId: newSticker.id,
          oldId: action.payload.body.id || action.payload.body.stickerId,
        })
      );
      yield put(removeOneFromHistory());
    }
  } catch (error) {
    if (error.response) {
      yield put(errorEdit(error.response.data.message));
    } else {
      yield put(errorEdit("Something has gone wrong. Please try again later"));
    }
  } finally {
    yield put(loadingEdit(false));
  }
}

function* updateOne(action) {
  yield put(loadingEdit(true));

  const body = {
    ...action.payload.body,
    stickerId: action.payload.body.id || action.payload.body.stickerId,
  };

  try {
    const response = yield call(stickersService.update, body);

    const updatedSticker = yield* enrichStickerWithColors(body);
    
    // Preserve students list from prevEvent if available
    const students = action.payload.attachedStudents || action.payload.prevEvent?.students || [];
    const updatedStickerWithStudents = {
      ...updatedSticker,
      students: students,
    };

    yield put(stickerUpdateOne(updatedStickerWithStudents));
    yield put(addChosenSticker(updatedStickerWithStudents));
    yield put(successEdit("The class has been updated"));

    const { teacherLevelAssignment } = response.data || {};
    if (teacherLevelAssignment && teacherLevelAssignment !== "ok") {
      yield put(
        changeNotificationHistory({
          ...action.payload.body,
          notification: teacherLevelAssignment,
        })
      );
      toast.warn(teacherLevelAssignment);
    }

    if (!action.payload.isUndo) {
      const event = {
        reversedType: "STICKER_UPDATE_ONE",
        eventBody: action.payload.prevEvent,
      };
      yield put(addOneToHistory(event));
      yield put(removeChosenSticker());
      yield put(contentEdit("The class has  been updated"));
      yield put(modalEdit("Confirm"));
    } else {
      yield put(removeOneFromHistory());
    }
  } catch (error) {
    if (error.response) {
      yield put(errorEdit(error.response.data.message));
    } else {
      yield put(errorEdit("Something has gone wrong. Please try again later"));
    }
  } finally {
    yield put(loadingEdit(false));
  }
}

function* removeOne(action) {
  yield put(loadingEdit(true));
  const body = action.payload.body ? action.payload.body : action.payload;
  try {
    yield call(stickersService.delete, {
      stickerId: body.stickerId || body.id,
      userId: body.userId,
    });
    yield put(stickerRemoveOne(body.stickerId || body.id));
    yield put(removeChosenSticker());

    if (!action.payload.isUndo) {
      const event = {
        reversedType: "STICKER_ADD_ONE",
        eventBody: body,
      };
      yield put(addOneToHistory(event));
      yield put(contentEdit("The class has been deleted"));
      yield put(modalEdit("Confirm"));
    } else {
      yield put(removeOneFromHistory());
    }
  } catch (error) {
    if (error.response) {
      yield put(errorEdit(error.response.data.message));
    } else {
      yield put(errorEdit("Something has gone wrong. Please try again later"));
    }
  } finally {
    yield put(loadingEdit(false));
  }
}

function* attachStudent(action) {
  try {
    const response = yield call(studentsService.addToSticker, action.payload.body);
    const { student: newStudent, created } = response.data || {};

    if (!newStudent) {
      throw new Error("Failed to attach student");
    }

    const studentPayload = created 
      ? [...action.payload.attachedStudents, newStudent] 
      : [...action.payload.attachedStudents.filter(student => student.id !== newStudent.id), newStudent];
    
    const stickerPayload = {
      ...action.payload.prevSticker,
      ...action.payload.stickerBody,
      students: studentPayload,
    };

    yield put(stickerUpdateOne(stickerPayload));
    yield put(addChosenSticker(stickerPayload));

    // Update student in state.students
    if (created) {
      yield put(studentAddOne({
        id: newStudent.id,
        name: newStudent.name,
        birthDate: newStudent.birthDate,
        regularStickers: Array.isArray(newStudent.regularStickers) ? newStudent.regularStickers : [],
      }));
    } else {
      const studentEntities = yield select((state) => state.students.entities || {});
      const studentEntity = studentEntities?.[newStudent.id];
      if (studentEntity) {
        yield put(studentUpdateOne({
          studentId: newStudent.id,
          name: newStudent.name,
          birthDate: newStudent.birthDate,
          regularStickers: Array.isArray(newStudent.regularStickers) ? newStudent.regularStickers : [],
        }));
      }
    }
  } catch (error) {
    if (error.response) {
      yield put(errorEdit(error.response.data.message));
    } else {
      yield put(errorEdit("Something has gone wrong. Please try again later"));
    }
    throw error;
  }
}

function* detachStudent(action) {
  try {
    const response = yield call(stickersService.update, action.payload.body);
    
    const updatedSticker = yield* enrichStickerWithColors(action.payload.body);
    const updatedStickerWithStudents = {
      ...updatedSticker,
      students: action.payload.attachedStudents || [],
    };

    yield put(stickerUpdateOne(updatedStickerWithStudents));
    yield put(addChosenSticker(updatedStickerWithStudents));

    // Update student in state.students
    const studentEntities = yield select((state) => state.students.entities || {});
    const studentEntity = studentEntities?.[action.payload.studentId];
    if (studentEntity) {
      const updatedStickers = (studentEntity.regularStickers || []).filter(
        (sticker) => +sticker.id !== +action.payload.body.id
      );
      const fallbackStudent = action.payload.prevSticker?.students?.find(
        (student) => +student.id === +action.payload.studentId
      ) || {};
      yield put(studentUpdateOne({
        studentId: action.payload.studentId,
        name: studentEntity.name,
        birthDate: studentEntity.birthDate ?? fallbackStudent.birthDate ?? null,
        regularStickers: updatedStickers,
      }));
    }
  } catch (error) {
    if (error.response) {
      yield put(errorEdit(error.response.data.message));
    } else {
      yield put(errorEdit("Something has gone wrong. Please try again later"));
    }
    throw error;
  }
}

export default function* watchStickersSaga() {
  yield takeLeading("STICKERS_GET_ALL", getAll);
  yield takeLeading("STICKER_ADD_ONE", addOne);
  yield takeLeading("STICKER_UPDATE_ONE", updateOne);
  yield takeLeading("STICKER_REMOVE_ONE", removeOne);
  yield takeLeading("STICKER_STUDENT_ATTACH", attachStudent);
  yield takeLeading("STICKER_STUDENT_DETACH", detachStudent);
}
